import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Megaphone, Download, Database, Upload, Filter, CircleDot, Mail } from "lucide-react";
import { csvToObjects } from "@/lib/csv";

const DAILY_STATUS_META = {
  draft: { label: "Draft", cls: "bg-[#F3EFEA] text-[#5C6661] border-[#E5E0D8]" },
  review: { label: "Review", cls: "bg-[#D4A373]/20 text-[#8a5a2a] border-[#D4A373]/50" },
  scheduled: { label: "Scheduled", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  published: { label: "Published", cls: "bg-[#2C4C3B]/10 text-[#2C4C3B] border-[#2C4C3B]/30" },
};
const STATUS_ORDER = ["draft", "review", "scheduled", "published"];

function StatusChip({ status, testid }) {
  const meta = DAILY_STATUS_META[status] || DAILY_STATUS_META.published;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.cls}`}><CircleDot className="w-3 h-3" /> {meta.label}</span>;
}

function StatusFilter({ value, onChange, testid }) {
  return (
    <div className="inline-flex bg-[#F3EFEA] border border-[#E5E0D8] rounded-full p-1 flex-wrap" data-testid={testid}>
      {["all", ...STATUS_ORDER].map((s) => (
        <button key={s} data-testid={`${testid}-${s}`} onClick={() => onChange(s)} className={`px-3 py-1 rounded-full text-xs font-medium ${value === s ? "bg-[#2C4C3B] text-white" : "text-[#1C201E]"}`}>
          {s === "all" ? "All" : DAILY_STATUS_META[s].label}
        </button>
      ))}
    </div>
  );
}

function StatusActions({ current, onChange, disabled, testid }) {
  return (
    <select
      data-testid={testid}
      value={current || "published"}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs px-2 py-1 rounded-full border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
    >
      {STATUS_ORDER.map((s) => <option key={s} value={s}>{DAILY_STATUS_META[s].label}</option>)}
    </select>
  );
}


function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function BulkUploadPanel({ kind, expectedHeaders, example, onDone }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | parsed | publishing | done
  const [report, setReport] = useState(null);
  const [showExample, setShowExample] = useState(false);
  const endpoint = kind === "verse" ? "/daily-verses/bulk" : "/devotions/bulk";
  const label = kind === "verse" ? "verses" : "devotions";

  const onFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const { rows: parsed } = csvToObjects(text, expectedHeaders);
    if (parsed.length === 0) { toast.error("CSV appears empty or headers didn't match."); return; }
    setRows(parsed);
    setStatus("parsed");
    setReport(null);
  };

  const downloadExample = () => {
    const blob = new Blob([example], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bbc-${kind}-example.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const publish = async () => {
    setStatus("publishing");
    try {
      const { data } = await api.post(endpoint, { items: rows });
      setReport(data);
      setStatus("done");
      toast.success(`Published ${data.published} ${label}${data.failed ? `, ${data.failed} failed` : ""}`);
      if (data.published > 0) onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk publish failed");
      setStatus("parsed");
    }
  };

  return (
    <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl" data-testid={`bulk-${kind}-panel`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2"><Upload className="w-5 h-5 text-[#2C4C3B]" />Weekly {kind === "verse" ? "Verse" : "Devotion"} Series</div>
          <div className="text-xs text-[#5C6661] mt-1">Upload a CSV to schedule a week (or a month) of {label} in one go. Headers required: <code>{expectedHeaders.join(",")}</code>. Each KJV reference is validated before publishing.</div>
        </div>
        <div className="flex gap-2">
          <button data-testid={`bulk-${kind}-example`} onClick={downloadExample} className="px-3 py-1.5 rounded-full border border-[#D4A373] text-[#8a5a2a] text-xs hover:bg-[#D4A373]/10">Download example</button>
          <button onClick={() => setShowExample((v) => !v)} className="px-3 py-1.5 rounded-full border border-[#E5E0D8] text-[#1C201E] text-xs hover:bg-[#F3EFEA]">{showExample ? "Hide" : "Show"} example</button>
        </div>
      </div>
      {showExample && (
        <pre className="mt-3 p-3 bg-[#F3EFEA] border border-[#E5E0D8] rounded-lg text-xs overflow-auto text-[#1C201E]">{example}</pre>
      )}

      <label className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm cursor-pointer hover:bg-[#2C4C3B] hover:text-white">
        <Upload className="w-4 h-4" /> Choose CSV file…
        <input data-testid={`bulk-${kind}-file`} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      </label>

      {status !== "idle" && rows.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-[#5C6661] mb-2">{rows.length} rows parsed. Preview first 5:</div>
          <div className="overflow-x-auto border border-[#E5E0D8] rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-[#F3EFEA]">
                <tr>{expectedHeaders.map((h) => <th key={h} className="text-left px-3 py-2 uppercase tracking-wider text-[#5C6661]">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={i} className="border-t border-[#E5E0D8]">
                    {expectedHeaders.map((h) => <td key={h} className="px-3 py-2 text-[#1C201E]">{(r[h] || "").slice(0, 60)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid={`bulk-${kind}-publish`} onClick={publish} disabled={status === "publishing"} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
            <Plus className="w-4 h-4" /> {status === "publishing" ? "Publishing..." : `Publish all ${rows.length} ${label}`}
          </button>
        </div>
      )}

      {report && (
        <div className="mt-4 p-4 border border-[#E5E0D8] rounded-lg" data-testid={`bulk-${kind}-report`}>
          <div className="text-sm text-[#1C201E]"><b className="text-[#2C4C3B]">{report.published}</b> published{report.failed > 0 && <> · <b className="text-red-600">{report.failed}</b> failed</>}</div>
          {report.results.filter((r) => r.status === "error").slice(0, 10).map((r, i) => (
            <div key={i} className="mt-1 text-xs text-red-600">Row {r.row} ({r.date} {r.reference || r.title}): {r.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}


export function DailyVerseTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: todayStr(), reference: "", status: "published" });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    setItems((await api.get(`/daily-verses/admin${q}`)).data || []);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const doPreview = async () => {
    if (!form.reference.trim()) return;
    try {
      const { data } = await api.get(`/bible/verse?ref=${encodeURIComponent(form.reference.trim())}`);
      // #region agent log
      fetch('http://127.0.0.1:7326/ingest/43a25cab-f050-49f0-855e-165f1e083422',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'a69cbb'},body:JSON.stringify({sessionId:'a69cbb',location:'AdminExtraTabs.jsx:doPreview',message:'bible preview ok',data:{ref:form.reference.trim(),hasText:!!data?.text},timestamp:Date.now(),hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      setPreview(data);
    } catch (e) {
      // #region agent log
      fetch('http://127.0.0.1:7326/ingest/43a25cab-f050-49f0-855e-165f1e083422',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'a69cbb'},body:JSON.stringify({sessionId:'a69cbb',location:'AdminExtraTabs.jsx:doPreview',message:'bible preview failed',data:{ref:form.reference.trim(),status:e?.response?.status,detail:e?.response?.data?.detail||e?.message},timestamp:Date.now(),hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      setPreview(null);
      toast.error(e?.response?.data?.detail || "Could not resolve reference");
    }
  };

  const add = async () => {
    if (!form.reference.trim()) { toast.error("Reference required"); return; }
    setSaving(true);
    try {
      await api.post("/daily-verses", form);
      setForm({ date: todayStr(), reference: "", status: "published" });
      setPreview(null);
      await load();
      toast.success(`Daily verse saved as ${form.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this daily verse?")) return;
    await api.delete(`/daily-verses/${id}`);
    await load();
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/daily-verses/${id}/status`, { status }); toast.success(`Set to ${status}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="daily-verse-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">Publish a Daily Verse</h3>
        <p className="text-xs text-[#5C6661] mb-4">Enter a KJV reference (e.g. <code>John 3:16</code> or <code>Romans 8:28-30</code>). The text auto-populates from the Bible and links to the KJV in-app. Save as <b>Draft</b> or <b>Review</b> for editorial workflows.</p>
        <div className="grid md:grid-cols-5 gap-3">
          <input data-testid="dv-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="dv-reference" placeholder="e.g. John 3:16" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} onBlur={doPreview} className="md:col-span-2 px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <select data-testid="dv-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]">
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{DAILY_STATUS_META[s].label}</option>)}
          </select>
          <button data-testid="dv-preview-btn" onClick={doPreview} className="px-4 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white">Preview KJV</button>
        </div>
        {preview && (
          <div data-testid="dv-preview" className="mt-4 p-4 rounded-xl bg-[#F3EFEA] border border-[#E5E0D8]">
            <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{preview.reference} (KJV)</div>
            <div className="mt-1 text-[#1C201E] italic">&ldquo;{preview.text}&rdquo;</div>
          </div>
        )}
        <button data-testid="dv-add-btn" onClick={add} disabled={saving} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
          <Plus className="w-4 h-4" /> {saving ? "Saving..." : `Save as ${DAILY_STATUS_META[form.status].label}`}
        </button>
      </div>

      <BulkUploadPanel
        kind="verse"
        expectedHeaders={["date", "reference"]}
        example={`date,reference
2026-03-01,John 3:16
2026-03-02,Psalm 23:1
2026-03-03,Romans 8:28
2026-03-04,Isaiah 40:31
2026-03-05,Philippians 4:13
2026-03-06,Proverbs 3:5-6
2026-03-07,Matthew 6:33`}
        onDone={load}
      />

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-serif-display text-2xl text-[#1C201E]">All Verses ({items.length})</h3>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#5C6661]" />
            <StatusFilter value={filter} onChange={setFilter} testid="dv-filter" />
          </div>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((v, i) => (
            <div key={v.id} data-testid={`dv-row-${i}`} className="py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{v.date}</span>
                  <StatusChip status={v.status || "published"} testid={`dv-status-${i}`} />
                  {v.ai_generated && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">AI · {v.ai_model}</span>}
                </div>
                <div className="font-medium text-[#1C201E] mt-1">{v.reference}</div>
                <div className="text-sm text-[#5C6661] mt-1 italic line-clamp-2">&ldquo;{v.text}&rdquo;</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusActions current={v.status} onChange={(s) => changeStatus(v.id, s)} testid={`dv-status-set-${i}`} />
                <button data-testid={`dv-del-${i}`} onClick={() => del(v.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No verses in this view.</div>}
        </div>
      </div>
    </div>
  );
}

export function DevotionTab() {
  const [items, setItems] = useState([]);
  const [deletingAudioId, setDeletingAudioId] = useState(null);
  const [form, setForm] = useState({ date: todayStr(), title: "", reference: "", message: "", application: "", status: "published" });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    setItems((await api.get(`/devotions/admin${q}`)).data || []);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const doPreview = async () => {
    if (!form.reference.trim()) return;
    try {
      const { data } = await api.get(`/bible/verse?ref=${encodeURIComponent(form.reference.trim())}`);
      setPreview(data);
    } catch (e) {
      setPreview(null);
      toast.error(e?.response?.data?.detail || "Could not resolve reference");
    }
  };

  const add = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title & message required"); return; }
    setSaving(true);
    try {
      await api.post("/devotions", form);
      setForm({ date: todayStr(), title: "", reference: "", message: "", application: "", status: "published" });
      setPreview(null);
      await load();
      toast.success(`Devotion saved as ${form.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this devotion?")) return;
    await api.delete(`/devotions/${id}`);
    await load();
  };

  const deleteAudio = async (id) => {
    if (!window.confirm("Delete this devotion's audio? You can generate new audio in the TTS tab.")) return;
    setDeletingAudioId(id);
    try {
      await api.delete(`/tts/devotion/${id}/audio`);
      setItems(prev => prev.map(d => d.id === id ? { ...d, audio_url: null } : d));
      toast.success("Audio deleted. Generate new audio in the TTS tab.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete audio");
    } finally {
      setDeletingAudioId(null);
    }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/devotions/${id}/status`, { status }); toast.success(`Set to ${status}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="devotion-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">Publish a Daily Devotion</h3>
        <p className="text-xs text-[#5C6661] mb-4">Date, title, KJV references (separate passages with semicolons), message, and an optional application/quotation. Editorial workflow: Draft → Review → Scheduled → Published.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="dev-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="dev-title" placeholder="Devotion title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        </div>
        <div className="mt-3 grid md:grid-cols-5 gap-3">
          <input data-testid="dev-reference" placeholder="e.g. Psalm 23:1; John 3:16" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} onBlur={doPreview} className="md:col-span-2 px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <select data-testid="dev-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]">
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{DAILY_STATUS_META[s].label}</option>)}
          </select>
          <button data-testid="dev-preview-btn" onClick={doPreview} className="md:col-span-2 px-4 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm hover:bg-[#2C4C3B] hover:text-white">Preview KJV</button>
        </div>
        {preview && (
          <div data-testid="dev-preview" className="mt-3 p-4 rounded-xl bg-[#F3EFEA] border border-[#E5E0D8]">
            <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{preview.reference} (KJV)</div>
            <div className="mt-1 text-[#1C201E] italic">&ldquo;{preview.text}&rdquo;</div>
          </div>
        )}
        <textarea data-testid="dev-message" rows={5} placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-3 w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        <textarea data-testid="dev-application" rows={3} placeholder="Application / Quotation (optional)" value={form.application} onChange={(e) => setForm({ ...form, application: e.target.value })} className="mt-3 w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        <button data-testid="dev-add-btn" onClick={add} disabled={saving} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
          <Plus className="w-4 h-4" /> {saving ? "Saving..." : `Save as ${DAILY_STATUS_META[form.status].label}`}
        </button>
      </div>

      <BulkUploadPanel
        kind="devotion"
        expectedHeaders={["date", "title", "reference", "message", "application"]}
        example={`date,title,reference,message,application
2026-03-01,Chosen in Christ,Ephesians 1:4,Before the foundation of the world God set His love on us.,Rest today in the fact that you are not an afterthought.
2026-03-02,Peace Beyond Understanding,Philippians 4:7,God's peace guards your heart and mind through Christ Jesus.,Cast one worry on Him right now.
2026-03-03,Renewed Strength,Isaiah 40:31,Those who wait on the LORD renew their strength.,Take five minutes in silent prayer before your first meeting.`}
        onDone={load}
      />

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-serif-display text-2xl text-[#1C201E]">All Devotions ({items.length})</h3>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#5C6661]" />
            <StatusFilter value={filter} onChange={setFilter} testid="dev-filter" />
          </div>
        </div>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((d, i) => (
            <div key={d.id} data-testid={`dev-row-${i}`} className="py-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{d.date} · {d.reference}</span>
                  <StatusChip status={d.status || "published"} testid={`dev-status-${i}`} />
                  {d.ai_generated && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">AI · {d.ai_model}</span>}
                  {d.audio_url && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Audio Narration</span>}
                </div>
                <div className="font-medium text-[#1C201E] mt-1">{d.title}</div>
                <div className="text-sm text-[#5C6661] mt-1 line-clamp-3">{d.message}</div>
                {d.audio_url && (
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <audio src={d.audio_url} controls className="h-8" />
                    <button onClick={() => deleteAudio(d.id)} disabled={deletingAudioId !== null}
                      className="inline-flex items-center gap-1 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-60">
                      <Trash2 className="w-3 h-3" /> {deletingAudioId === d.id ? "Deleting audio..." : "Delete audio"}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <StatusActions current={d.status} onChange={(s) => changeStatus(d.id, s)} testid={`dev-status-set-${i}`} />
                <button data-testid={`dev-del-${i}`} onClick={() => del(d.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No devotions in this view.</div>}
        </div>
      </div>
    </div>
  );
}

export function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: todayStr(), title: "", body: "", visibility: "both" });
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/announcements")).data || []);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title & message required"); return; }
    setSaving(true);
    try {
      await api.post("/announcements", form);
      setForm({ date: todayStr(), title: "", body: "", visibility: "both" });
      await load();
      toast.success("Announcement published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="announcements-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1 flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#D4A373]" />Publish an Announcement</h3>
        <p className="text-xs text-[#5C6661] mb-4">Shown on the website home page and/or pushed to mobile app users.</p>
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="ann-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="ann-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2 px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <select
            data-testid="ann-visibility"
            value={form.visibility}
            onChange={(e) => setForm({ ...form, visibility: e.target.value })}
            className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
          >
            <option value="both">Both Web & App</option>
            <option value="web">Website Only</option>
            <option value="app">Mobile App Only</option>
          </select>
        </div>
        <textarea data-testid="ann-body" rows={4} placeholder="Message" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-3 w-full px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        <button data-testid="ann-add-btn" onClick={add} disabled={saving} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
          <Plus className="w-4 h-4" /> {saving ? "Publishing..." : "Publish"}
        </button>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-4">All Announcements ({items.length})</h3>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((a, i) => (
            <div key={a.id} data-testid={`ann-row-${i}`} className="py-4 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{a.date}</span>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${
                    a.visibility === 'web' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    a.visibility === 'app' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                    'bg-green-50 text-green-700 border-green-200'
                  }`}>
                    {a.visibility === 'web' ? 'Website' : a.visibility === 'app' ? 'Mobile App' : 'Web & App'}
                  </span>
                </div>
                <div className="font-medium text-[#1C201E] mt-1">{a.title}</div>
                <div className="text-sm text-[#5C6661] mt-1 whitespace-pre-line line-clamp-3">{a.body}</div>
              </div>
              <button data-testid={`ann-del-${i}`} onClick={() => del(a.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No announcements yet.</div>}
        </div>
      </div>
    </div>
  );
}

export function GroupsTab({ currentUser }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", member_ids: [] });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteText, setInviteText] = useState("");

  const load = async () => {
    setGroups((await api.get("/groups")).data || []);
    setUsers((await api.get("/users")).data || []);
  };
  useEffect(() => { load(); }, []);

  const toggle = (uid) => {
    setForm((f) => ({
      ...f,
      member_ids: f.member_ids.includes(uid) ? f.member_ids.filter((x) => x !== uid) : [...f.member_ids, uid],
    }));
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Group name required"); return; }
    setSaving(true);
    try {
      await api.post("/groups", form);
      setForm({ name: "", description: "", member_ids: [] });
      await load();
      toast.success("Group created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/groups/seed-defaults");
      toast.success(data.count ? `${data.count} default groups created` : "Default groups already exist");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSeeding(false); }
  };

  const submitInvite = async () => {
    const emails = inviteText.split(/[\n,;\s]+/).map((s) => s.trim()).filter((s) => /@/.test(s));
    if (emails.length === 0) { toast.error("Add at least one valid email"); return; }
    try {
      const { data } = await api.post(`/groups/${inviteFor.id}/invite`, { emails });
      toast.success(`Added ${data.added.length}, invited ${data.pending.length}`);
      setInviteFor(null); setInviteText("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    await api.delete(`/groups/${id}`);
    await load();
  };

  const setMembers = async (g, uid, add) => {
    const nextMembers = add ? Array.from(new Set([...(g.member_ids || []), uid])) : (g.member_ids || []).filter((x) => x !== uid);
    try {
      await api.put(`/groups/${g.id}`, { member_ids: nextMembers });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="groups-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-serif-display text-lg text-[#1C201E]">Ministry starter pack</div>
          <div className="text-xs text-[#5C6661]">Create the four default groups: Youth Ministry, Choir &amp; Music, Deacons, Prayer Team.</div>
        </div>
        <button data-testid="grp-seed-defaults" onClick={seedDefaults} disabled={seeding} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4A373] text-white text-sm hover:bg-[#b3854e] disabled:opacity-60">
          {seeding ? "Seeding..." : "Seed default groups"}
        </button>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">Create Group Chat</h3>
        <p className="text-xs text-[#5C6661] mb-4">Members can belong to multiple groups. Only admins &amp; super admins can create groups.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <input data-testid="grp-name" placeholder="Group name (e.g. Youth Ministry)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
          <input data-testid="grp-desc" placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" />
        </div>
        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661] mb-2">Assign members ({form.member_ids.length} selected)</div>
          <div className="max-h-56 overflow-auto grid sm:grid-cols-2 md:grid-cols-3 gap-1 p-2 bg-[#F3EFEA] rounded-lg">
            {users.filter((u) => (u.user_id !== currentUser?.user_id)).map((u, i) => (
              <label key={u.user_id} data-testid={`grp-user-${i}`} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white cursor-pointer text-sm">
                <input type="checkbox" checked={form.member_ids.includes(u.user_id)} onChange={() => toggle(u.user_id)} />
                <span className="truncate">{u.name} <span className="text-xs text-[#5C6661]">({u.role === "bible_school_student" ? "Bible School Student" : u.role})</span></span>
              </label>
            ))}
          </div>
        </div>
        <button data-testid="grp-create-btn" onClick={create} disabled={saving} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
          <Plus className="w-4 h-4" /> {saving ? "Creating..." : "Create Group"}
        </button>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-2xl text-[#1C201E] mb-4">Groups ({groups.length})</h3>
        <div className="divide-y divide-[#E5E0D8]">
          {groups.map((g, i) => (
            <div key={g.id} data-testid={`grp-row-${i}`} className="py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium text-[#1C201E]">
                    {g.name}
                    {g.is_default && <span className="ml-2 text-[10px] bg-[#D4A373]/20 text-[#8a5a2a] px-2 py-0.5 rounded-full uppercase tracking-widest">default</span>}
                    {g.is_general && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full uppercase tracking-widest">general</span>}
                  </div>
                  {g.description && <div className="text-xs text-[#5C6661] mt-0.5">{g.description}</div>}
                  <div className="text-xs text-[#5C6661] mt-1">{(g.member_ids || []).length} members · {(g.invited_emails || []).length} pending invites</div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`grp-invite-${i}`} onClick={() => { setInviteFor(g); setInviteText(""); }} className="px-3 py-1.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-xs hover:bg-[#2C4C3B] hover:text-white">Bulk invite</button>
                  <button data-testid={`grp-del-${i}`} onClick={() => removeGroup(g.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <details className="mt-3">
                <summary className="text-xs text-[#2C4C3B] cursor-pointer">Manage members</summary>
                <div className="mt-2 max-h-48 overflow-auto grid sm:grid-cols-2 gap-1 p-2 bg-[#F3EFEA] rounded-lg">
                  {users.map((u) => {
                    const isMember = (g.member_ids || []).includes(u.user_id);
                    return (
                      <label key={u.user_id} className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white cursor-pointer text-sm">
                        <input type="checkbox" checked={isMember} onChange={() => setMembers(g, u.user_id, !isMember)} />
                        <span className="truncate">{u.name} <span className="text-xs text-[#5C6661]">({u.role === "bible_school_student" ? "Bible School Student" : u.role})</span></span>
                      </label>
                    );
                  })}
                </div>
              </details>
              {(g.invited_emails || []).length > 0 && (
                <div className="mt-2 text-xs text-[#5C6661]">Pending: {(g.invited_emails || []).slice(0, 6).join(", ")}{(g.invited_emails || []).length > 6 && ` +${g.invited_emails.length - 6} more`}</div>
              )}
            </div>
          ))}
          {groups.length === 0 && <div className="text-sm text-[#5C6661]">No groups yet.</div>}
        </div>
      </div>

      {inviteFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50" onClick={() => setInviteFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-lg w-full">
            <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1">Invite to {inviteFor.name}</h3>
            <p className="text-xs text-[#5C6661] mb-3">Paste one email per line (or comma-separated). Existing users are added immediately; new emails receive a friendly invite.</p>
            <textarea data-testid="grp-invite-textarea" value={inviteText} onChange={(e) => setInviteText(e.target.value)} rows={6} className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#D4A373]" placeholder="one@church.org, two@example.com" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setInviteFor(null)} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm">Cancel</button>
              <button data-testid="grp-invite-submit" onClick={submitInvite} className="px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm">Send invites</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function BibleWarmTab() {  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const warmBible = async () => {
    setStatus("running");
    const books = (await api.get("/bible/books")).data;
    const total = books.reduce((n, b) => n + b.chapters, 0);
    setProgress({ done: 0, total });
    let done = 0;
    for (const b of books) {
      for (let c = 1; c <= b.chapters; c++) {
        try { await api.get(`/bible/${encodeURIComponent(b.name)}/${c}`); } catch {}
        done += 1;
        setProgress({ done, total });
      }
    }
    setStatus("done");
    toast.success("KJV warmed for offline search");
  };

  return (
    <div data-testid="bible-warm-tab" className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
      <h3 className="font-serif-display text-2xl text-[#1C201E] mb-1 flex items-center gap-2"><BookOpen className="w-5 h-5 text-[#D4A373]" />Bible (KJV) — Warm Cache</h3>
      <p className="text-xs text-[#5C6661] mb-4">Bible verses are loaded on demand from the public KJV source and cached in the database. Warming the cache once makes site search &amp; mobile Bible instant for every user afterward.</p>
      <button data-testid="warm-bible-btn" onClick={warmBible} disabled={status === "running"} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
        {status === "running" ? `Warming ${progress.done}/${progress.total}…` : status === "done" ? "Done — run again?" : "Warm the KJV Bible"}
      </button>
      {status === "running" && (
        <div className="mt-3 h-2 bg-[#F3EFEA] rounded-full overflow-hidden">
          <div className="h-full bg-[#2C4C3B] transition-all" style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} />
        </div>
      )}
    </div>
  );
}

export function BackupsTab() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => setItems((await api.get("/backups")).data || []);
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/backups/run");
      toast.success(`Backup ${data.filename} — ${(data.size_bytes / 1024).toFixed(1)} KB, ${data.docs} docs`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setRunning(false); }
  };

  const download = async (filename) => {
    try {
      const resp = await api.get(`/backups/${encodeURIComponent(filename)}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6" data-testid="backups-tab">
      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-serif-display text-2xl text-[#1C201E] flex items-center gap-2"><Database className="w-5 h-5 text-[#2C4C3B]" />CMS Auto-Backups</div>
          <div className="text-xs text-[#5C6661] mt-1">A snapshot runs automatically at 03:00 UTC every night. Last 30 kept. Contains users, content, sermons, events, prayers, daily verses, devotions, announcements, groups, messages, files &amp; transactions.</div>
        </div>
        <button data-testid="backup-run-btn" onClick={runNow} disabled={running} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm hover:bg-[#1A3326] disabled:opacity-60">
          {running ? "Running..." : "Run backup now"}
        </button>
      </div>

      <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
        <h3 className="font-serif-display text-xl text-[#1C201E] mb-3">History ({items.length})</h3>
        <div className="divide-y divide-[#E5E0D8]">
          {items.map((b, i) => (
            <div key={b.id} data-testid={`backup-row-${i}`} className="py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium text-[#1C201E] text-sm truncate">{b.filename}</div>
                <div className="text-xs text-[#5C6661] mt-0.5">{new Date(b.created_at).toLocaleString()} · {(b.size_bytes / 1024).toFixed(1)} KB · {b.docs} docs</div>
              </div>
              <button data-testid={`backup-download-${i}`} onClick={() => download(b.filename)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-xs hover:bg-[#2C4C3B] hover:text-white">
                <Download className="w-3 h-3" /> Download
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[#5C6661]">No backups yet — click <b>Run backup now</b> to create the first one.</div>}
        </div>
      </div>
    </div>
  );
}


export function MessagesVisibilityTab() {
  const [groups, setGroups] = useState([]);
  const [threads, setThreads] = useState([]);
  const [viewing, setViewing] = useState(null); // { type: 'group' | 'dm', id: string, name: string }
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [g, t] = await Promise.all([
        api.get("/groups"),
        api.get("/admin/dm/threads"),
      ]);
      setGroups(g.data || []);
      setThreads(t.data || []);
    } catch (e) {
      toast.error("Failed to load messaging data");
    }
  };

  useEffect(() => { load(); }, []);

  const viewGroup = async (g) => {
    setLoading(true);
    setViewing({ type: "group", id: g.id, name: g.name });
    try {
      const { data } = await api.get(`/groups/${g.id}/messages`);
      setMessages(data.messages || []);
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };

  const viewDm = async (t) => {
    setLoading(true);
    const names = t.users.map(u => u.name).join(" & ");
    setViewing({ type: "dm", id: t.thread_key, name: names });
    try {
      const { data } = await api.get(`/admin/dm/messages/${t.thread_key}`);
      setMessages(data || []);
    } catch { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };

  const deleteMessage = async (message) => {
    if (!window.confirm("Delete message?\n\nThis message will be removed from the conversation for ordinary users.")) return;
    try {
      const { data } = await api.delete(`/chat/messages/${message.id}`);
      setMessages(prev => prev.map(m => m.id === data.message.id ? { ...m, ...data.message } : m));
      toast.success("Message deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete message");
    }
  };

  return (
    <div className="space-y-6" data-testid="messages-visibility-tab">
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-6">
          <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
            <h3 className="font-serif-display text-xl text-[#1C201E] mb-4">Group Chats</h3>
            <div className="divide-y divide-[#E5E0D8] max-h-[400px] overflow-auto">
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => viewGroup(g)}
                  className={`w-full text-left py-3 px-2 hover:bg-[#F3EFEA] rounded-lg transition-colors ${viewing?.id === g.id ? "bg-[#F3EFEA]" : ""}`}
                >
                  <div className="font-medium text-[#1C201E] text-sm">{g.name}</div>
                  <div className="text-xs text-[#5C6661]">{(g.member_ids || []).length} members</div>
                </button>
              ))}
              {groups.length === 0 && <div className="text-sm text-[#5C6661] py-4">No groups.</div>}
            </div>
          </div>

          <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl">
            <h3 className="font-serif-display text-xl text-[#1C201E] mb-4">Direct Messages</h3>
            <div className="divide-y divide-[#E5E0D8] max-h-[400px] overflow-auto">
              {threads.map((t) => (
                <button
                  key={t.thread_key}
                  onClick={() => viewDm(t)}
                  className={`w-full text-left py-3 px-2 hover:bg-[#F3EFEA] rounded-lg transition-colors ${viewing?.id === t.thread_key ? "bg-[#F3EFEA]" : ""}`}
                >
                  <div className="font-medium text-[#1C201E] text-sm">{t.users.map(u => u.name).join(" & ")}</div>
                  <div className="text-xs text-[#5C6661] truncate">{t.last_text}</div>
                  <div className="text-[10px] text-[#8A8F8B] mt-1">{new Date(t.last_at).toLocaleString()}</div>
                </button>
              ))}
              {threads.length === 0 && <div className="text-sm text-[#5C6661] py-4">No DM threads.</div>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="p-6 bg-white border border-[#E5E0D8] rounded-xl min-h-[600px] flex flex-col">
            {viewing ? (
              <>
                <div className="flex items-center justify-between border-b border-[#E5E0D8] pb-4 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#D4A373]">{viewing.type === 'group' ? 'Group Chat' : 'Direct Message'}</div>
                    <h3 className="font-serif-display text-2xl text-[#1C201E]">{viewing.name}</h3>
                  </div>
                  <button onClick={() => setViewing(null)} className="text-sm text-[#5C6661] hover:text-[#1C201E]">Close</button>
                </div>

                {loading ? (
                  <div className="flex-1 flex items-center justify-center text-[#5C6661]">Loading messages…</div>
                ) : (
                  <div className="flex-1 overflow-auto space-y-4 pr-2">
                    {messages.map((m) => (
                      <div key={m.id} className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold text-sm text-[#2C4C3B]">{m.user_name || m.sender_name}</span>
                          <span className="text-[10px] text-[#8A8F8B]">{new Date(m.created_at).toLocaleString()}</span>
                          {m.is_deleted && <span className="text-[9px] font-black tracking-widest text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">DELETED</span>}
                        </div>
                        <div className={`mt-1 p-3 rounded-2xl rounded-tl-none text-sm inline-block self-start max-w-[85%] whitespace-pre-line ${m.is_deleted ? "bg-red-50 border border-red-200 text-red-800" : "bg-[#F3EFEA] text-[#1C201E]"}`}>
                          <div>{m.text}</div>
                          {m.image_url && (
                            <img src={m.image_url.startsWith("/") ? (process.env.REACT_APP_BACKEND_URL + m.image_url) : m.image_url} alt="" className="mt-2 rounded-lg max-h-60 object-contain" />
                          )}
                          {m.is_deleted && (
                            <div className="mt-2 text-[10px] font-semibold text-red-700">
                              {m.deletion_type === "moderation" ? "Moderator deleted" : "Sender deleted"}
                              {m.deleted_by_name ? ` by ${m.deleted_by_name}` : ""}
                              {m.deleted_at ? ` · ${new Date(m.deleted_at).toLocaleString()}` : ""}
                            </div>
                          )}
                        </div>
                        {!m.is_deleted && (
                          <button
                            onClick={() => deleteMessage(m)}
                            className="mt-1 text-[11px] text-red-600 hover:text-red-800 inline-flex items-center gap-1 self-start"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    ))}
                    {messages.length === 0 && <div className="text-center py-10 text-[#5C6661]">No messages in this thread.</div>}
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center flex-col text-center opacity-40">
                <Mail className="w-12 h-12 mb-4" />
                <div className="font-serif-display text-xl">Select a conversation to view</div>
                <p className="text-xs mt-1">Super Admin Exclusive Visibility</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
